import { useState, useEffect, useCallback, useRef } from 'react';
import { consultarSesion, type UsuarioSesion } from '@/lib/sesion';

export function useAuth() {
  const [user, setUser] = useState<UsuarioSesion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    consultarSesion()
      .then(setUser)
      .catch((err) => {
        setUser(null);
        setError(err?.message || 'Error de conexión con el servidor');
      })
      .finally(() => setLoading(false));
  }, []);

  return { user, loading, error };
}

export function useApi<T>(fetcher: () => Promise<T>, deps: any[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * Guardia de obsolescencia: solo la última llamada puede escribir estado.
   *
   * Sin esto gana la respuesta que llega la última, no la que se pidió la
   * última. En pantallas con muchos filtros —Huéspedes tiene nueve controles
   * apuntando al mismo endpoint— dos clics seguidos dejaban en la tabla las
   * filas de la petición anterior mientras el pie decía otra página.
   */
  const generacion = useRef(0);

  const refresh = useCallback(() => {
    const mia = ++generacion.current;
    setLoading(true);
    setError(null);
    fetcher()
      .then(d => { if (mia === generacion.current) setData(d); })
      .catch(err => { if (mia === generacion.current) setError(err.message); })
      .finally(() => { if (mia === generacion.current) setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
